/**
 * Brush bed-bundle reconciler — keeps knead-cover-brush on the
 * "Bed Bundle Exclusive" variant while an eligible bed is in the cart,
 * reverting to "Standard" once the last bed is gone.
 *
 * Algorithm: assets/brush-bundle-core.js (KQBrushBundleCore)
 * Config:   snippets/brush-bundle-config.liquid (window.KQ_BRUSH_BUNDLE)
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.KQBrushBundleCore) return;
  var Core = window.KQBrushBundleCore;
  var cartUrl = (window.theme && window.theme.routes.cart_url) || '/cart';
  var rootUrl = (window.theme && window.theme.routes.root_url) || '/';

  function readConfig() {
    var c = window.KQ_BRUSH_BUNDLE;
    if (!c || !c.PRODUCT_ID || !c.STANDARD_VARIANT_ID || !c.BUNDLE_VARIANT_ID) return null;
    return {
      productId: c.PRODUCT_ID,
      standardId: c.STANDARD_VARIANT_ID,
      bundleId: c.BUNDLE_VARIANT_ID,
      bedProductIds: c.BED_PRODUCT_IDS || []
    };
  }

  function fetchCart() {
    return fetch(cartUrl + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      return response.ok
        ? response.json()
        : response.text().then(function (body) { throw new Error('cart ' + response.status + ' ' + body); });
    });
  }

  function postCart(path, body) {
    // BOGOS watches every /cart/(add|change|update) call to decide whether an
    // offer needs re-applying, and it re-opens the free-gift popup on each one.
    // This swap is our own pricing fix-up, not a shopper cart change, so it
    // carries the marker BOGOS already honours to skip a request. Without it
    // the popup opens again for every write in the swap.
    return fetch(rootUrl + path + '?_bogos-ignore=1', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.ok
        ? response.json()
        : response.text().then(function (text) { throw new Error(path + ' ' + response.status + ' ' + text); });
    });
  }

  // The slider cart and the main cart page both render line items inside a
  // <cart-items> web component. Refresh that subtree so brush variant swaps
  // show in either surface. Replace main-cart-footer too because the subtotal
  // follows the variant.
  function refreshCartUI() {
    var currentMain = document.getElementById('main-cart-items');
    var currentFooter = document.getElementById('main-cart-footer');
    if (!currentMain && !currentFooter) return Promise.resolve();
    return fetch(cartUrl + '?_kq_brush=' + Date.now(), {
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('cart html ' + response.status);
      return response.text();
    }).then(function (html) {
      var next = new DOMParser().parseFromString(html, 'text/html');
      var currentCartItems = currentMain && currentMain.querySelector('cart-items, .js-contents');
      var nextCartItems = next.querySelector('#main-cart-items cart-items, #main-cart-items .js-contents');
      if (currentCartItems && nextCartItems) currentCartItems.innerHTML = nextCartItems.innerHTML;
      var nextFooter = next.getElementById('main-cart-footer');
      if (currentFooter && nextFooter) currentFooter.innerHTML = nextFooter.innerHTML;
      var liveRegion = document.getElementById('cart-live-region-text');
      if (!liveRegion) return;
      // Announce the final subtotal after the variant swap, using theme translations.
      return fetch(cartUrl + '?section_id=cart-live-region-text&_kq_brush=' + Date.now(), {
        headers: { Accept: 'text/html' },
        cache: 'no-store'
      }).then(function (response) {
        if (!response.ok) throw new Error('cart announcement ' + response.status);
        return response.text();
      }).then(function (announcement) {
        liveRegion.textContent = new DOMParser().parseFromString(announcement, 'text/html').body.textContent.trim();
        liveRegion.setAttribute('aria-hidden', 'false');
      });
    }).catch(function (error) {
      console.warn('[kq-brush-bundle] cart UI refresh failed:', error);
    });
  }

  var inflight = null;
  var pending = false;
  var syncing = false;
  var checkingOut = false;
  var failed = false;
  var blockedControls = new Map();

  function updateCheckoutState() {
    var blocked = syncing || checkingOut || failed;
    if (blocked) {
      document.querySelectorAll('.cart__dynamic-checkout-buttons, #mini-cart .additional-checkout-buttons').forEach(function (element) {
        if (!blockedControls.has(element)) blockedControls.set(element, element.inert);
        element.inert = true;
      });
    } else {
      blockedControls.forEach(function (wasInert, element) { element.inert = wasInert; });
      blockedControls.clear();
    }
  }

  function showError(error) {
    var region = document.getElementById('cart-errors');
    if (!region) return;
    region.setAttribute('role', 'alert');
    region.textContent = error ? window.theme.cartStrings.error : '';
  }

  function synchronize() {
    if (!readConfig()) return Promise.resolve();
    pending = true;
    if (inflight) return inflight;
    syncing = true;
    updateCheckoutState();
    // Never overlap writes: a cancelled HTTP request can still mutate Shopify's cart.
    inflight = Promise.resolve().then(async function () {
      try {
        do {
          pending = false;
          var changed = false;
          await Core.reconcile({
            config: readConfig(),
            getCart: fetchCart,
            post: postCart,
            changed: function () { changed = true; }
          });
          if (changed) await refreshCartUI();
        } while (pending);
        failed = false;
        showError(null);
      } catch (error) {
        failed = true;
        showError(error);
        throw error;
      } finally {
        inflight = null;
        syncing = false;
        updateCheckoutState();
      }
    });
    return inflight;
  }

  function schedule() {
    synchronize().catch(function (error) {
      console.warn('[kq-brush-bundle] reconcile failed:', error);
    });
  }

  async function checkout(continuation, control) {
    if (checkingOut) return;
    checkingOut = true;
    control.setAttribute('aria-busy', 'true');
    updateCheckoutState();
    try {
      // Keep the existing optional cover check, then require final brush prices.
      if (window.KQFreeCoverSync) {
        await window.KQFreeCoverSync.validateBeforeCheckout().catch(function () {});
      }
      await synchronize();
      continuation();
    } catch (error) {
      showError(error);
      console.warn('[kq-brush-bundle] checkout paused:', error);
    } finally {
      checkingOut = false;
      control.removeAttribute('aria-busy');
      updateCheckoutState();
    }
  }

  // Run before the theme's document-level handlers, which navigate independently.
  window.addEventListener('click', function (event) {
    if (!readConfig()) return;
    var link = event.target.closest && event.target.closest('a[href*="/checkout"]');
    if (!link || !link.closest('#main-cart-footer, #mini-cart, cart-items, .cart__footer')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(function () { window.location.assign(link.href); }, link);
  }, true);

  window.addEventListener('submit', function (event) {
    if (!readConfig()) return;
    var submitter = event.submitter || document.activeElement;
    if (!submitter || submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var form = event.target;
    checkout(function () {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = submitter.name;
      input.value = submitter.value || 'Checkout';
      form.appendChild(input);
      form.submit();
      form.removeChild(input);
    }, submitter);
  }, true);

  function init() {
    if (!readConfig()) return;
    // Cart rendering replaces the accelerated-payment container while requests run.
    new MutationObserver(updateCheckoutState).observe(document.body, { childList: true, subtree: true });
    schedule();
  }

  ['ajaxProduct:added', 'cart:updated'].forEach(function (eventName) {
    document.addEventListener(eventName, schedule);
  });
  // pageshow covers bfcache restores, where the DOM survives but no cart
  // event fires.
  window.addEventListener('pageshow', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
